import React, { useEffect, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import {
  calculateCashItemsDetails,
  calculateInsuranceItemsDetails,
  getApplicableInsuranceLimit
} from './VisitItemsCalculations'
import { getFixedCardCost } from '../../../constants/VisitsConstants'
import { FaHandHoldingUsd } from 'react-icons/fa'

const Field = ({ label, value, bold = false, accent = false, info = false }) => {
  const { t } = useTranslation()
  return (
    <div
      className={`flex items-center gap-1 xl:gap-2 px-1.5 xl:px-2.5 py-0.5 xl:py-1 rounded ${accent ? 'bg-primary/10' : ''}`}
    >
      <span
        className={`text-[10px] md:text-[11px] xl:text-[13px] whitespace-nowrap font-semibold uppercase ${accent ? 'font-bold text-primary' : info ? 'text-gray-600' : 'text-gray-400'}`}
      >
        {t(label)}:
      </span>
      <span
        className={`tabular-nums tracking-tight text-[12px] md:text-sm lg:text-md xl:text-lg ${accent
          ? '!font-bold text-primary'
          : info || bold
            ? 'font-semibold text-gray-800'
            : 'font-medium text-gray-600'}`}
      >
        {value}
      </span>
    </div>
  )
}
const BillSummary = ({
  patientType,
  selectedPayerData,
  selectedPatientData,
  cardOptions = [],
  depositBalance,
  updateDepositValues = false,
  className = '',
  invoiceDiscount = 0,
  followUp,
  episodeRemainingLimit = 0,
  episodeMaxLimit = 0,
  maxLimitOverride = null
}) => {
  const { values, setFieldValue } = useFormikContext()
  const { t } = useTranslation()

  const cardCost = useMemo(
    () => getFixedCardCost(cardOptions, values?.cardType),
    [cardOptions, values?.cardType]
  )

  const cashDetails = useMemo(
    () => calculateCashItemsDetails(values, cardCost),
    [values?.items, values?.deduct_from_deposit, cardCost]
  )

  const insuranceDetails = useMemo(
    () =>
      calculateInsuranceItemsDetails(
        values,
        getApplicableInsuranceLimit(
          selectedPayerData,
          episodeRemainingLimit,
          episodeMaxLimit,
          maxLimitOverride
        ),
        selectedPatientData,
        cardCost
      ),
    [
      values?.items,
      values?.deduct_from_deposit,
      selectedPayerData,
      selectedPatientData,
      cardCost,
      followUp,
      episodeRemainingLimit,
      episodeMaxLimit,
      maxLimitOverride
    ]
  )

  useEffect(() => {
    if (!updateDepositValues) return

    if (depositBalance > 0) {
      setFieldValue('deduct_from_deposit', true)
      setFieldValue('paymentMethod', 'Deposit')
      setFieldValue('cardType', '')
      setFieldValue('cards', [])
    } else {
      setFieldValue('deduct_from_deposit', false)
      setFieldValue('paymentMethod', 'card')
      setFieldValue('cardType', 'Mada')
    }
  }, [depositBalance, selectedPatientData])

  const isCash = patientType === 'cash'
  const data = isCash ? cashDetails : insuranceDetails

  const gross = isCash
    ? cashDetails?.totals?.totalGrossAmount?.toFixed(2)
    : insuranceDetails.totals?.totalGrossAmount?.toFixed(2)

  const discount = isCash
    ? values?.items
      ?.filter(itm => !itm?.low_stock && !itm?.out_of_stock)
      ?.map(itm => {
        const discountAmount = Number(itm?.discount_amount) || 0
        const discountType = itm?.discount_type
        let discount = 0

        if (discountType === 'percentage') {
          discount =
            (Number(itm.unit_price) * Number(itm.quantity) * discountAmount) /
            100
        } else if (discountType === 'fixed') {
          discount = discountAmount
        }

        return discount
      })
      .reduce((acc, curr) => acc + curr, 0)
      ?.toFixed(2)
    : insuranceDetails.totals?.discountAmountTotal?.toFixed(2)

  const payerNet = isCash ? '0.00' : insuranceDetails.totals?.companyShareTotal
  const payerVat = isCash ? '0.00' : insuranceDetails.totals?.companyTaxTotal
  const payerTotal = isCash
    ? '0.00'
    : (
      parseFloat(insuranceDetails.totals?.companyShareTotal || 0) +
      parseFloat(insuranceDetails.totals?.companyTaxTotal || 0)
    )?.toFixed(2)

  const patNet = isCash
    ? cashDetails?.totals?.patientShareTotal
    : insuranceDetails.totals?.patientShareTotal
  const patVat = isCash
    ? cashDetails?.totals?.patientTaxTotal
    : insuranceDetails.totals?.patientTaxTotal
  const patTotal = isCash
    ? Number(
      cashDetails?.totals?.netPayableAmount +
      cashDetails?.totals?.patientTaxTotal || 0
    ).toFixed(2)
    : Number(
      insuranceDetails?.totals?.patientShareTotal +
      insuranceDetails?.totals?.patientTaxTotal || 0
    ).toFixed(2)

  return (
    <div className={`w-full ${className}`}>
      <div className='w-full border border-gray-100 bg-gray-100 rounded-t-lg'>
        <div className='px-3 xl:px-4 py-1.5'>
          <div className='flex flex-wrap items-center justify-between gap-y-2 w-full'>
            <div className='flex items-center gap-4 border-l-2 border-gray-300 pl-3 md:pl-4 first:border-0 first:pl-0'>
              <Field label='Gross' value={gross} info />
              <Field label='Discount' value={discount} info />
            </div>

            <div className='flex flex-wrap items-center gap-x-6 md:gap-x-8 gap-y-2'>
              <div className='flex items-center gap-3.5 border-l-2 border-blue-400 pl-4'>
                <span className='text-[10px] xl:text-[11px] font-semibold text-blue-700 uppercase tracking-tight'>
                  {t('Company')}
                </span>
                <Field label='Net' value={payerNet} />
                <Field label='VAT' value={payerVat} />
                <Field label='Total' value={payerTotal} bold />
              </div>

              <div className='flex items-center gap-3.5 border-l-2 border-blue-600 pl-4'>
                <span className='text-[10px] xl:text-[11px] font-semibold text-blue-800 uppercase tracking-tight'>
                  {t('Patient')}
                </span>
                <Field label='Net' value={patNet} />
                <Field label='VAT' value={patVat} />
                <Field label='Total' value={patTotal} accent />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default React.memo(BillSummary)
